import {
  DermloopUserRoleEnum,
  PatientIdTypeEnum,
} from '@dermloop/static-values';
import { Type } from 'class-transformer';
import {
  ArrayNotEmpty,
  IsBoolean,
  IsDate,
  IsEnum,
  IsNotEmpty,
  IsNumberString,
  IsOptional,
  IsString,
  IsUUID,
  Length,
  MaxDate,
  ValidateNested,
} from 'class-validator';
import { DermloopUserMustResetPasswordEnum } from '../user.dto';

export enum SeedMedicalUnitAffiliationInvitationEnum {
  SINGLE = 'Single',
  MULTIPLE = 'Multiple',
}

export class SeedCiUserData {
  @IsOptional()
  @IsString()
  firstName?: string | null | undefined;

  @IsOptional()
  @IsString()
  lastName?: string | null | undefined;
}
export class SeedCiUserDto {
  // User roles for CI user
  @IsEnum(DermloopUserRoleEnum, { each: true })
  ciUserRoles: DermloopUserRoleEnum[];

  @IsOptional()
  @IsEnum(DermloopUserMustResetPasswordEnum)
  ciUserPasswordState?: DermloopUserMustResetPasswordEnum;

  @IsOptional()
  @ValidateNested()
  userData?: SeedCiUserData;
}

export class SeedDto {
  //Settings from which to seed the current user
  @IsNotEmpty()
  @Type(() => SeedCiUserDto)
  @ValidateNested({ each: true })
  ciUserOptions: SeedCiUserDto;

  // Optional patients to create
  @IsOptional()
  @Type(() => SeedPatientDto)
  @ValidateNested({ each: true })
  patients?: SeedPatientDto[];

  // Optional device id to use for CI user
  @IsOptional()
  @IsString()
  deviceId?: string;

  // Set to true if seeder should make an extra affiliation with invitationAccepted: false
  @IsOptional()
  @IsEnum(SeedMedicalUnitAffiliationInvitationEnum)
  createMedicalUnitAffiliationInvitation?: SeedMedicalUnitAffiliationInvitationEnum;

  /**
   * Used to create health records and conditions/triages that are made by another medical unit. Triggers break the glass functionality
   */
  @IsOptional()
  @IsBoolean()
  areHealthRecordsFromCIUserMedicalUnit?: boolean;

  /**
   * Used to create health records and conditions/triages that are made by another medical unit in another jurisdictional group. Triggers break the glass forbidden error
   */
  @IsOptional()
  @IsBoolean()
  areHealthRecordsFromCIUserJurisdictionalGroup?: boolean;

  @IsOptional()
  @IsEnum(PatientIdTypeEnum)
  patientIdType?: PatientIdTypeEnum;

  @IsOptional()
  @IsBoolean()
  requireEconsultManualRelease?: boolean;
}

export enum SeedGenderEnum {
  MALE = 'Male',
  FEMALE = 'Female',
  OTHER = 'Other',
}
export class SeedPatientDto {
  @IsOptional()
  @IsUUID()
  id?: string;

  // CPR of patient. If none provided, generates random cpr
  @IsOptional()
  @IsNumberString()
  @Length(10, 10)
  ssn?: string;

  // If no gender provided, uses random gender
  @IsOptional()
  @IsEnum(SeedGenderEnum)
  gender?: SeedGenderEnum;

  // Conditions to create for patient
  @Type(() => SeedConditionDto)
  @ValidateNested({ each: true })
  conditions: SeedConditionDto[];

  // Optional health record information. Values not provided are generated at random
  @IsOptional()
  @IsBoolean()
  familyHistoryWithMelanoma?: boolean;

  @IsOptional()
  @IsBoolean()
  formerSkinCancer?: boolean;

  @IsOptional()
  @Type(() => SeedRiskFactorDto)
  @ValidateNested({ each: true })
  riskFactors?: SeedRiskFactorDto[];
}

export class SeedConditionDto {
  // Date of condition. If not provided, uses random past date
  @IsOptional()
  @IsDate()
  // Dates cannot be in the future
  @MaxDate(new Date())
  date?: Date;

  @Type(() => SeedConditionTriageDto)
  @ArrayNotEmpty()
  @ValidateNested({ each: true })
  triages: SeedConditionTriageDto[];
}

export class SeedRiskFactorDto {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsNotEmpty()
  name_da: string;

  @IsString()
  @IsNotEmpty()
  name_de: string;
}

export enum SeedConditionActionEnum {
  REFERRAL = 'Referral',
  FOLLOW_UP = 'FollowUp',
  DISMISSAL = 'Dismissal',
  BIOPSY = 'Biopsy',
  NEW_IMAGE_REGISTRATION = 'NewImageRegistration',
  NO_ACTION = 'NoAction',
}

export class SeedConditionTriageDto {
  // Date of condition triage. If not provided, uses random past date
  @IsOptional()
  @IsDate()
  // Dates cannot be in the future
  @MaxDate(new Date())
  date?: Date;

  @IsOptional()
  @IsEnum(SeedConditionActionEnum)
  action?: SeedConditionActionEnum;

  // Creates diagnosis with name and sets for corresponding action
  // If no diagnosis name is provided, action will not have a diagnosis
  @IsOptional()
  @IsString()
  @IsNotEmpty()
  diagnosisName?: string;

  // Creates diagnosis with name and sets for corresponding action
  // If no diagnosis name is provided, action will not have a diagnosis
  @IsOptional()
  @IsString()
  @IsNotEmpty()
  secondaryDiagnosisName?: string;

  // Creates diagnosis with name and sets for corresponding action
  // If no diagnosis name is provided, action will not have a diagnosis
  @IsOptional()
  @IsString()
  @IsNotEmpty()
  tertiaryDiagnosisName?: string;
}
